'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import { Client } from './ClientTeamContent';

interface Props {
  open: boolean;
  onClose: () => void;
  clients: Client[];
}

interface InviteFormData {
  fullName: string;
  email: string;
  role: 'admin' | 'manager' | 'editor';
  message: string;
}

const roleDescriptions = {
  admin: 'Full access to all clients, campaigns, team management, and settings.',
  manager: 'Can create and manage campaigns, schedule content, and view analytics for assigned clients.',
  editor: 'Can create and schedule content posts for assigned clients. Cannot create campaigns or view billing.',
};

export default function InviteTeamMemberModal({ open, onClose, clients }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<InviteFormData>({
    defaultValues: { role: 'editor' },
  });

  const selectedRole = watch('role');

  const toggleClient = (clientId: string) => {
    setSelectedClients((prev) =>
      prev.includes(clientId) ? prev.filter((c) => c !== clientId) : [...prev, clientId]
    );
  };

  const onSubmit = async (data: InviteFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/team/invite
    await new Promise((r) => setTimeout(r, 1200));
    toast.success(`Invitation sent to ${data.email}`);
    setIsSubmitting(false);
    reset();
    setSelectedClients([]);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Invite Team Member" subtitle="Send an invitation to join your agency workspace" size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-base">Full Name</label>
              <input
                type="text"
                placeholder="Alex Johnson"
                className={`input-base ${errors.fullName ? 'border-negative' : ''}`}
                {...register('fullName', { required: 'Full name is required' })}
              />
              {errors.fullName && <p className="error-text">{errors.fullName.message}</p>}
            </div>
            <div>
              <label className="label-base">Work Email</label>
              <input
                type="email"
                placeholder="alex@agency.com"
                className={`input-base ${errors.email ? 'border-negative' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>
          </div>

          <div>
            <label className="label-base">Role</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {(['admin', 'manager', 'editor'] as const).map((role) => (
                <label
                  key={`invite-role-${role}`}
                  className={`flex flex-col p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
                    selectedRole === role ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}
                >
                  <input type="radio" value={role} {...register('role')} className="sr-only" />
                  <span className={`text-xs font-bold capitalize mb-1 ${
                    role === 'admin' ? 'text-primary' : role === 'manager' ? 'text-accent' : 'text-muted-foreground'
                  }`}>{role}</span>
                </label>
              ))}
            </div>
            {selectedRole && (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{roleDescriptions[selectedRole]}</p>
            )}
          </div>

          {selectedRole !== 'admin' && (
            <div>
              <label className="label-base">Assign to Clients</label>
              <p className="helper-text">Select which client accounts this member can access</p>
              <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto scrollbar-thin border border-border rounded-lg p-2">
                {clients.map((client) => (
                  <label
                    key={`assign-${client.id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => toggleClient(client.id)}
                      className="w-4 h-4 rounded border-input accent-primary"
                    />
                    <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">{client.name.charAt(0)}</span>
                    </div>
                    <span className="text-sm text-foreground">{client.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{client.industry}</span>
                  </label>
                ))}
              </div>
              {selectedClients.length > 0 && (
                <p className="text-xs text-primary mt-1">{selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected</p>
              )}
            </div>
          )}

          <div>
            <label className="label-base">Personal Message (optional)</label>
            <textarea
              rows={2}
              placeholder="Welcome to the team! Looking forward to working with you…"
              className="input-base resize-none"
              {...register('message')}
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending…
              </span>
            ) : (
              'Send Invitation'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}